import { forwardRef, useId, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/shared/lib/cn';
import { labelClass } from '@/shared/ui/core/styles';

export type RadioOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type RadioGroupProps = ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
  label?: string;
  options?: RadioOption[];
  orientation?: 'vertical' | 'horizontal';
};

export type RadioGroupItemProps = ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>;

export const RadioGroupItem = forwardRef<
  ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(function RadioGroupItem({ className, ...props }, ref) {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-border-strong bg-surface transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
        'data-[state=checked]:border-primary',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="size-2.5 rounded-full bg-primary" />
    </RadioGroupPrimitive.Item>
  );
});

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  { className, label, options, children, orientation = 'vertical', id, ...props },
  ref,
) {
  const autoId = useId();
  const groupId = id ?? autoId;

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <div id={groupId} className={labelClass}>
          {label}
        </div>
      ) : null}
      <RadioGroupPrimitive.Root
        ref={ref}
        aria-labelledby={label ? groupId : undefined}
        className={cn(
          'flex gap-3',
          orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
          className,
        )}
        {...props}
      >
        {options
          ? options.map((opt) => (
              <label
                key={opt.value}
                className={cn(
                  'inline-flex cursor-pointer items-start gap-2.5',
                  opt.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <RadioGroupItem value={opt.value} disabled={opt.disabled} />
                <span className="flex flex-col gap-0.5">
                  <span className="text-body text-text">{opt.label}</span>
                  {opt.description ? (
                    <span className="text-caption text-text-secondary">{opt.description}</span>
                  ) : null}
                </span>
              </label>
            ))
          : children}
      </RadioGroupPrimitive.Root>
    </div>
  );
});

/** Alias matching Prompt Pack name `Radio`. */
export const Radio = RadioGroup;
